import {
  Base,
  createApp,
  importWhenVisible,
  importWhenPrefersMotion,
} from '@studiometa/js-toolkit';
import { Figure } from '@studiometa/ui';
import Header from './components/Header.js';
import About from './components/About.js';
import ArticleModal from './components/ArticleModal.js';
import ParallaxMedia from './components/ParallaxMedia.js';
import Fade from './components/Fade.js';
import NewsletterForm from './components/NewsletterForm.js';
import TitleReveal from './components/TitleReveal.js';
import StaggerReveal from './components/StaggerReveal.js';
import ButtonRoll from './components/ButtonRoll.js';
import { isDev } from './config.js';

/**
 * @typedef {{
 *   $children: {
 *     Figure: Figure[],
 *     Header: Header[],
 *     About: About[],
 *     ArticleModal: ArticleModal[],
 *     TitleReveal: TitleReveal[],
 *     StaggerReveal: StaggerReveal[],
 *     ButtonRoll: ButtonRoll[],
 *     Video: Array<Promise<import('./components/Video.js').default>>
 *     Accordion: Array<Promise<import('./components/Accordion.js').default>>
 *     Slider: Array<Promise<import('./components/Slider.js').default>>
 *     Conversations: Array<Promise<import('./components/Conversations.js').default>>
 *     PhotographiesItem: Array<Promise<import('./components/PhotographiesItem.js').default>>
 *     Scenographie: Array<Promise<import('./components/Scenographie.js').default>>
 *     Librairie: Array<Promise<import('./components/Librairie.js').default>>
 *     SmoothScroll: Array<Promise<import('./components/SmoothScroll.js').default>>
 *   }
 * }} AppProps
 */

/**
 * App class.
 * @augments {Base<AppProps>}
 */
class App extends Base {
  /**
   * App config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'App',
    log: isDev(),
    components: {
      Figure,
      Header,
      About,
      ArticleModal,
      NewsletterForm,
      '[data-fade]': Fade,
      'h1, h2, h3': TitleReveal,
      '[data-stagger-reveal]': StaggerReveal,
      '.btn': ButtonRoll,
      Slider: () => importWhenVisible(() => import('./components/Slider.js'), 'Slider'),
      Video: () => importWhenVisible(() => import('./components/Video.js'), 'Video'),
      Accordion: () => importWhenVisible(() => import('./components/Accordion.js'), 'Accordion'),
      Conversations: () =>
        importWhenVisible(() => import('./components/Conversations.js'), 'Conversations'),
      PhotographiesItem: () =>
        importWhenVisible(() => import('./components/PhotographiesItem.js'), 'PhotographiesItem'),
      Scenographie: () =>
        importWhenVisible(() => import('./components/Scenographie.js'), 'Scenographie'),
      Librairie: () => importWhenVisible(() => import('./components/Librairie.js'), 'Librairie'),
      // Smooth scroll (Lenis) chargé uniquement si l'utilisateur n'a pas activé
      // prefers-reduced-motion. Lenis scrolle nativement : les `sticky` CSS
      // restent fonctionnels.
      SmoothScroll: () => importWhenPrefersMotion(() => import('./components/SmoothScroll.js')),
      ParallaxMedia,
    },
  };
}

export default createApp(App, {
  root: document.documentElement,
  breakpoints: {
    '2xs': '0rem',
    xs: '30rem',
    s: '40rem',
    m: '48rem',
    l: '64rem',
    xl: '80rem',
    '2xl': '96rem',
    '3xl': '120rem',
  },
});
